import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import { useIntl, history } from 'umi';
import { Form, Input, notification, Radio, Button, Modal } from 'tea-component';
import TipContext from '@home/components/TipContextComponents/TipContext';
import { DASHBOARD_NAME_REGEX } from '@dbl/core/global';
import './index.scss';
import { useRequest } from 'ahooks';
import { copyDashboard } from '@dbl/services/dashboard';

export enum ChartInDashboardCopyType {
  COPY = 'copy',
  REFER = 'refer',
}

interface CopyDashboardFormProps {
  dashboardName: string;
  chartCopyType: ChartInDashboardCopyType;
  onFromValid: (valid: boolean) => void;
}

interface CopyDashboardFormRef {
  getFormValues: () => { name: string; chartCopy: ChartInDashboardCopyType; };
  setNameRepeat: (value: string) => void;
}


interface CopyDashboardModalProps {
  gameCode: string;
  dashboardId: string;
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void; // 复制成功后的 操作
  dashboardName: string;
  chartCopyType: ChartInDashboardCopyType;
  isReplaceDashboard?: boolean; // 是否替换原dashboard
  showContact: boolean; // 是否显示联系人
}

const CopyDashboardForm = forwardRef<CopyDashboardFormRef, CopyDashboardFormProps>((props, ref) => {
  const { dashboardName, chartCopyType, onFromValid } = props;
  const { formatMessage } = useIntl();
  const [name, setName] = useState(`${dashboardName}(Copy)`);
  const [chartCopy, setChartCopy] = useState<ChartInDashboardCopyType>(chartCopyType);
  const [nameValid, setNameValid] = useState<string>('');

  // 暴露给外部的方法
  useImperativeHandle(ref, () => ({
    getFormValues: () => ({
      name: name.trim(),
      chartCopy,
    }),
    setNameRepeat: (value: string) => {setNameValid(value)},
  }));

  const handleNameChange = (value: string) => {
    const tempName = value?.trim();
    if (tempName?.length < 1) {
      setNameValid('dbl_common_cannot_be_empty');
    } else if (tempName?.length > 50) {
      setNameValid('dbl_common_valid_name_length');
    } else if (!DASHBOARD_NAME_REGEX.test(tempName)) {
      setNameValid('dbl_common_dashboard_valid_name');
    } else {
      setNameValid('');
    }
    setName(value);
  };

  const handleChartCopyChange = (value: ChartInDashboardCopyType) => {
    setChartCopy(value);
  };

  useEffect(() => {
    onFromValid?.(!nameValid && !!name?.trim());
  }, [nameValid, name]);

  return (
    <Form>
      <Form.Item
        label={formatMessage({ id: 'dbl_dashboard_copy_name' })}
        showStatusIcon={false}
        required
        status={!nameValid ? undefined : 'error'}
        message={nameValid && formatMessage({ id: nameValid }, { count: 50 })}
      >
        <Input
          className={'dbl-copy-dashboard-form-input-name'}
          value={name}
          onChange={(value) => handleNameChange(value)}
        />
      </Form.Item>
      <Form.Item
        className={'dbl-copy-dashboard-form-chart-copy'}
        label={
          <>
            {formatMessage({ id: 'dbl_dashboard_copy_charts_in' })}
            <TipContext
              AWidth={400}
              tipText={formatMessage({ id: 'dbl_dashboard_copy_charts_in_info' })}
            />
          </>
        }
      >
        <Radio.Group value={chartCopy} onChange={(value) => handleChartCopyChange(value as ChartInDashboardCopyType)}>
          <Radio name={ChartInDashboardCopyType.COPY}>
            {formatMessage({ id: 'dbl_dashboard_copy_charts_in_copy' })}
          </Radio>
          <Radio name={ChartInDashboardCopyType.REFER}>
            {formatMessage({ id: 'dbl_dashboard_copy_charts_in_refer' })}
          </Radio>
        </Radio.Group>
      </Form.Item>
    </Form>
  );
});



const CopyDashboardModal: React.FC<CopyDashboardModalProps> = (props) => {
  const { visible, gameCode, onClose, dashboardId, onSuccess, isReplaceDashboard, showContact, ...restProps } = props;
  const { formatMessage } = useIntl();
  const formRef = React.useRef<CopyDashboardFormRef>(null);
  const [isDisabledConfirm, setIsDisabledConfirm] = useState<boolean>(false);


  const {loading, run} = useRequest(copyDashboard, {
    manual: true,
    onSuccess: (data: {id: string; name: string;}) => {
      onClose();
      const { id='', name='' } = data || {};
      const message = (<>
        <span>{formatMessage({id: 'dbl_dashboard_copy_success'})}</span>
        <Button type="link" onClick={() => {
          const params = {
            pathname: `/datalab/${gameCode}/WorkBench/MyDashboard/ViewPage`,
            query: {
              dashboardId: String(id),
              dashboardName: name,
              isTemplate: 'false',
            }
          };
          isReplaceDashboard ? history.replace(params) : history.push(params);
        }}>{name}</Button>
      </>);
      notification.success({className: 'dbl-dashboard-copy-notification', description: message, duration: 3000});
      onSuccess?.();
    },
    onError: (error) => {
      if (error?.code === 10011) {
        formRef.current?.setNameRepeat('dbl_dashboard_copy_name_repeat');
        return;
      }
      notification.error({description: formatMessage({id: 'dbl_dashboard_copy_fail'}), duration: 3000});
    },
  });

  return (
    <Modal
      visible={visible}
      disableCloseIcon={loading}
      onClose={onClose}
      caption={formatMessage({ id: 'dbl_dashboard_copy_title' })}
      className={'dbl-dashboard-copy-modal'}
    >
      <Modal.Body>
        <CopyDashboardForm ref={formRef} onFromValid={(valid) => { setIsDisabledConfirm(!valid)}} {...restProps} />
      </Modal.Body>
      <Modal.Footer>
        <Button loading={loading} type="primary" disabled={isDisabledConfirm} onClick={() => {
          const {name='', chartCopy='refer'} = formRef?.current?.getFormValues() || {};
          run({
            game_code: gameCode,
            name,
            id: String(dashboardId) || '',
            copy_chart: chartCopy,
            show_contact: showContact,
          });
        }}>
          {formatMessage({ id: 'dbl_common_confirm' })}
        </Button>
        <Button type="weak" disabled={loading} onClick={onClose}>
          {formatMessage({ id: 'dbl_common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CopyDashboardModal;